import React, { useState, useEffect, useCallback } from "react";
import { listTestCases as getTestCases, deleteTestCase } from "../api/testCases.js";
import { listTestRuns as getTestRuns, runTestCase, runBatch } from "../api/testRuns.js";
import { listPatients as getPatients } from "../api/patients.js";
import { listOutboundTasks, resetOutboundTasks, reseedOutboundTasks } from "../api/outboundTasks.js";
import TestCaseTable from "../components/TestCaseTable.jsx";
import LiveCallStatus from "../components/LiveCallStatus.jsx";
import SnapshotPanel from "../components/SnapshotPanel.jsx";

export default function Dashboard({ navigate }) {
  const [testCases, setTestCases] = useState([]);
  const [patients, setPatients] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState(new Set());
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [tasksMenuOpen, setTasksMenuOpen] = useState(false);
  const [tasksBusy, setTasksBusy] = useState(false);
  const [tasksSummary, setTasksSummary] = useState(null);

  const loadTasksSummary = async () => {
    try {
      const d = await listOutboundTasks("all");
      setTasksSummary({ total: d.total, byStatus: d.by_status || {} });
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadTasksSummary(); }, []);

  const handleResetTasks = async () => {
    if (!confirm("Reset all completed/skipped outbound tasks back to pending?\n\nThis keeps the task list intact but marks them all pending so you can rerun tests.")) return;
    setTasksBusy(true);
    try {
      const r = await resetOutboundTasks();
      alert(`Reset ${r.modified} task(s) back to pending.`);
      await loadTasksSummary();
    } catch (err) { alert("Reset failed: " + err.message); }
    finally { setTasksBusy(false); setTasksMenuOpen(false); }
  };

  const handleReseedTasks = async () => {
    if (!confirm("Wipe ALL outbound tasks and regenerate fresh ones from seed logic?\n\nThis deletes every task (including any custom ones) and creates new tasks for every patient based on their appointments and condition.")) return;
    setTasksBusy(true);
    try {
      const r = await reseedOutboundTasks();
      alert(`Reseeded: deleted ${r.deleted}, inserted ${r.inserted} across ${r.patients} patients.`);
      await loadTasksSummary();
    } catch (err) { alert("Reseed failed: " + err.message); }
    finally { setTasksBusy(false); setTasksMenuOpen(false); }
  };

  // Live confirmation modal
  const [liveModal, setLiveModal] = useState(null); // { action, label, ids } or null
  const [liveConfirmText, setLiveConfirmText] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [tc, r, p] = await Promise.all([getTestCases(), getTestRuns(), getPatients()]);
      setTestCases(tc);
      setRuns(r);
      setPatients(p);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (activeCalls.length === 0) return;
    const interval = setInterval(refresh, 5000);
    return () => clearInterval(interval);
  }, [activeCalls.length, refresh]);

  // ─── Run handlers (configMode-aware) ──────────────────────────────────

  const startRun = async (testCaseId, configMode) => {
    try {
      setActiveCalls((prev) => [...prev, testCaseId]);
      await runTestCase(testCaseId, configMode);
      const poll = setInterval(async () => {
        const updatedRuns = await getTestRuns();
        setRuns(updatedRuns);
        const latest = updatedRuns.find((r) => r.testCaseId === testCaseId);
        if (latest && latest.status !== "running") {
          setActiveCalls((prev) => prev.filter((id) => id !== testCaseId));
          clearInterval(poll);
        }
      }, 3000);
    } catch (err) {
      alert("Failed to start test: " + err.message);
      setActiveCalls((prev) => prev.filter((id) => id !== testCaseId));
    }
  };

  const startBatch = async (ids, configMode) => {
    setActiveCalls(ids);
    try {
      await runBatch(ids, configMode);
      const poll = setInterval(async () => {
        const updatedRuns = await getTestRuns();
        setRuns(updatedRuns);
        const allDone = ids.every((id) => {
          const latest = updatedRuns.find((r) => r.testCaseId === id);
          return latest && latest.status !== "running";
        });
        if (allDone) { setActiveCalls([]); clearInterval(poll); }
      }, 3000);
    } catch (err) {
      alert("Batch failed: " + err.message);
      setActiveCalls([]);
    }
  };

  // Draft actions (no confirmation needed)
  const handleRunDraft = (testCaseId) => startRun(testCaseId, "draft");
  const handleRunSelectedDraft = () => {
    if (selectedIds.size === 0) return;
    startBatch(Array.from(selectedIds), "draft");
  };
  const handleRunAllDraft = () => {
    const enabledIds = testCases.filter((tc) => tc.enabled).map((tc) => tc.id);
    if (enabledIds.length === 0) return;
    startBatch(enabledIds, "draft");
  };

  // Live actions (require confirmation modal)
  const requestLiveRun = (testCaseId) => {
    const tc = testCases.find((t) => t.id === testCaseId);
    setLiveModal({ action: "single", label: tc?.name || testCaseId, ids: [testCaseId] });
    setLiveConfirmText("");
  };
  const requestLiveSelected = () => {
    if (selectedIds.size === 0) return;
    setLiveModal({ action: "batch", label: `${selectedIds.size} selected test case(s)`, ids: Array.from(selectedIds) });
    setLiveConfirmText("");
  };
  const requestLiveAll = () => {
    const enabledIds = testCases.filter((tc) => tc.enabled).map((tc) => tc.id);
    if (enabledIds.length === 0) return;
    setLiveModal({ action: "all", label: `all ${enabledIds.length} enabled test case(s)`, ids: enabledIds });
    setLiveConfirmText("");
  };

  const confirmLiveRun = () => {
    if (liveConfirmText !== "YES") return;
    const { ids } = liveModal;
    setLiveModal(null);
    setLiveConfirmText("");
    if (ids.length === 1) startRun(ids[0], "live");
    else startBatch(ids, "live");
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this test case?")) return;
    await deleteTestCase(id);
    refresh();
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Build patient lookup map
  const patientMap = {};
  patients.forEach((p) => {
    patientMap[p.id] = p;
    if (p.patientCode) patientMap[p.patientCode] = p;
  });

  // Get last run result for each test case
  const lastRunMap = {};
  for (const tc of testCases) {
    const tcRuns = runs.filter((r) => r.testCaseId === tc.id);
    if (tcRuns.length > 0) lastRunMap[tc.id] = tcRuns[0];
  }

  // All unique tags across test cases, sorted with counts
  const tagCounts = {};
  for (const tc of testCases) {
    for (const t of tc.tags || []) tagCounts[t] = (tagCounts[t] || 0) + 1;
  }
  const allTags = Object.keys(tagCounts).sort();

  const toggleTag = (tag) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };

  // Filter: text search AND every selected tag must be present on the case
  const filtered = testCases.filter((tc) => {
    if (selectedTags.size > 0) {
      const caseTags = new Set(tc.tags || []);
      for (const t of selectedTags) if (!caseTags.has(t)) return false;
    }
    const q = search.toLowerCase().trim();
    if (!q) return true;
    const pat = tc.patientId ? patientMap[tc.patientId] : null;
    const parts = [
      tc.name, tc.description, tc.patientId,
      pat ? `${pat.firstName || ""} ${pat.lastName || ""}` : null,
      tc.persona ? `${tc.persona.firstName || ""} ${tc.persona.lastName || ""}` : null,
      ...(tc.tags || []),
    ];
    return parts.filter(Boolean).some((s) => s.toLowerCase().includes(q));
  });

  if (loading) return <div style={s.loading}>Loading...</div>;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <h1 style={s.title}>Test Cases</h1>
        <div style={s.headerActions}>
          {selectedIds.size > 0 && (
            <>
              <button style={s.btnDraft} onClick={handleRunSelectedDraft}>
                Draft ({selectedIds.size})
              </button>
              <button style={s.btnLive} onClick={requestLiveSelected}>
                Live ({selectedIds.size})
              </button>
            </>
          )}
          <button style={s.btnDraft} onClick={handleRunAllDraft}>Run All Draft</button>
          <button style={s.btnLive} onClick={requestLiveAll}>Run All Live</button>
          <div style={s.tasksMenuWrap}>
            <button style={s.btnOutline} onClick={() => setTasksMenuOpen(!tasksMenuOpen)} disabled={tasksBusy}>
              {tasksBusy ? "Working…" : `Tasks${tasksSummary ? ` (${tasksSummary.total})` : ""}`}
            </button>
            {tasksMenuOpen && (
              <div style={s.tasksMenu} onMouseLeave={() => setTasksMenuOpen(false)}>
                {tasksSummary && (
                  <div style={s.tasksSummary}>
                    {Object.entries(tasksSummary.byStatus).map(([k, v]) => (
                      <span key={k} style={s.tasksSummaryItem}>
                        <span style={s.tasksSummaryLabel}>{k}</span>
                        <span style={s.tasksSummaryCount}>{v}</span>
                      </span>
                    ))}
                  </div>
                )}
                <button style={s.tasksMenuItem} onClick={handleResetTasks}>
                  <div style={s.tasksMenuTitle}>Reset completed → pending</div>
                  <div style={s.tasksMenuSub}>Keeps the list. Marks all completed/skipped back to pending.</div>
                </button>
                <button style={s.tasksMenuItem} onClick={handleReseedTasks}>
                  <div style={s.tasksMenuTitle}>Reseed all tasks</div>
                  <div style={s.tasksMenuSub}>Wipes every task and regenerates from seed logic.</div>
                </button>
              </div>
            )}
          </div>
          <button style={s.btnOutline} onClick={() => setSnapshotOpen(!snapshotOpen)}>Versions</button>
          <button style={s.btnPrimary} onClick={() => navigate("editor")}>+ New Test Case</button>
        </div>
      </div>

      {/* Snapshot Panel */}
      {snapshotOpen && (
        <SnapshotPanel
          onClose={() => setSnapshotOpen(false)}
          onRestore={() => { setSnapshotOpen(false); refresh(); }}
        />
      )}

      {/* Search */}
      <div style={s.searchWrap}>
        <svg style={s.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input style={s.search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search test cases..." />
      </div>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div style={s.tagBar}>
          <span style={s.tagBarLabel}>Tags:</span>
          {allTags.map((tag) => {
            const active = selectedTags.has(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                style={active ? s.tagChipActive : s.tagChip}
              >
                {tag}
                <span style={active ? s.tagChipCountActive : s.tagChipCount}>{tagCounts[tag]}</span>
              </button>
            );
          })}
          {selectedTags.size > 0 && (
            <button type="button" style={s.tagClear} onClick={() => setSelectedTags(new Set())}>
              Clear
            </button>
          )}
          <span style={s.tagBarCount}>
            {filtered.length} of {testCases.length}
          </span>
        </div>
      )}

      {/* Active Calls */}
      {activeCalls.length > 0 && (
        <LiveCallStatus testCaseIds={activeCalls} testCases={testCases} />
      )}

      {/* Table */}
      <TestCaseTable
        testCases={filtered}
        patientMap={patientMap}
        lastRunMap={lastRunMap}
        selectedIds={selectedIds}
        activeCalls={activeCalls}
        onToggleSelect={handleToggleSelect}
        onRun={handleRunDraft}
        onRunLive={requestLiveRun}
        onEdit={(id) => navigate("editor", { testCaseId: id })}
        onDelete={handleDelete}
        onViewRun={(runId) => navigate("run-detail", { runId })}
      />

      {/* Live Confirmation Modal */}
      {liveModal && (
        <div style={s.modalOverlay} onClick={() => setLiveModal(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalIcon}>⚠</div>
            <h2 style={s.modalTitle}>Run Against LIVE</h2>
            <p style={s.modalText}>
              You are about to run <strong>{liveModal.label}</strong> against the <strong style={{ color: "#dc2626" }}>LIVE (production)</strong> configuration.
              This will use published prompts and production settings.
            </p>
            <p style={s.modalText}>
              Type <strong>YES</strong> to confirm:
            </p>
            <input
              style={s.modalInput}
              value={liveConfirmText}
              onChange={(e) => setLiveConfirmText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmLiveRun()}
              placeholder="Type YES to confirm"
              autoFocus
            />
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setLiveModal(null)}>Cancel</button>
              <button
                style={liveConfirmText === "YES" ? s.modalConfirm : s.modalConfirmDisabled}
                onClick={confirmLiveRun}
                disabled={liveConfirmText !== "YES"}
              >
                Run Live
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: "0" },
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "20px 32px 0",
  },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  headerActions: { display: "flex", gap: 6 },
  btnPrimary: {
    padding: "7px 14px", background: "#0f0f0f", color: "#fff", border: "1px solid #0f0f0f",
    borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  btnOutline: {
    padding: "7px 14px", background: "#fff", color: "#0f0f0f", border: "1px solid #e5e5e5",
    borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  tasksMenuWrap: { position: "relative" },
  tasksMenu: {
    position: "absolute", top: "calc(100% + 4px)", right: 0, width: 320,
    background: "#fff", border: "1px solid #e5e5e5", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50,
    padding: 6,
  },
  tasksSummary: {
    display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 10px",
    borderBottom: "1px solid #f0f0f0", marginBottom: 4,
  },
  tasksSummaryItem: {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 11, padding: "2px 8px", background: "#f5f5f5", borderRadius: 10,
  },
  tasksSummaryLabel: { color: "#666", textTransform: "uppercase", letterSpacing: "0.03em", fontWeight: 500 },
  tasksSummaryCount: { color: "#0f0f0f", fontWeight: 600 },
  tasksMenuItem: {
    display: "block", width: "100%", textAlign: "left", padding: "10px 12px",
    background: "transparent", border: "none", borderRadius: 6, cursor: "pointer",
  },
  tasksMenuTitle: { fontSize: 13, fontWeight: 500, color: "#0f0f0f" },
  tasksMenuSub: { fontSize: 11, color: "#999", marginTop: 2, lineHeight: 1.4 },
  btnDraft: {
    padding: "7px 14px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
    borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  btnLive: {
    padding: "7px 14px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca",
    borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
  searchWrap: { position: "relative", margin: "16px 32px 0" },
  searchIcon: { position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" },
  search: {
    width: "100%", padding: "9px 12px 9px 36px", border: "1px solid #e5e5e5",
    borderRadius: 8, fontSize: 14, outline: "none", background: "#fff", color: "#0f0f0f",
  },
  // Tag filter bar
  tagBar: {
    display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
    margin: "10px 32px 0",
  },
  tagBarLabel: { fontSize: 12, fontWeight: 500, color: "#666", marginRight: 2 },
  tagChip: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 10px", background: "#fff", color: "#333",
    border: "1px solid #e5e5e5", borderRadius: 999,
    fontSize: 12, fontWeight: 500, cursor: "pointer",
  },
  tagChipActive: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "4px 10px", background: "#0f0f0f", color: "#fff",
    border: "1px solid #0f0f0f", borderRadius: 999,
    fontSize: 12, fontWeight: 500, cursor: "pointer",
  },
  tagChipCount: { fontSize: 10, color: "#999", background: "#f5f5f5", padding: "1px 6px", borderRadius: 8, fontWeight: 500 },
  tagChipCountActive: { fontSize: 10, color: "#0f0f0f", background: "#fff", padding: "1px 6px", borderRadius: 8, fontWeight: 600 },
  tagClear: {
    padding: "4px 10px", background: "transparent", color: "#1d4ed8",
    border: "none", fontSize: 12, fontWeight: 500, cursor: "pointer",
  },
  tagBarCount: { marginLeft: "auto", fontSize: 12, color: "#999" },
  // Modal
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 12, padding: "28px 32px", maxWidth: 440, width: "100%",
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  },
  modalIcon: { fontSize: 36, textAlign: "center", marginBottom: 8 },
  modalTitle: { fontSize: 18, fontWeight: 600, textAlign: "center", marginBottom: 12 },
  modalText: { fontSize: 14, color: "#555", lineHeight: 1.6, marginBottom: 12, textAlign: "center" },
  modalInput: {
    width: "100%", padding: "10px 14px", border: "2px solid #dc2626", borderRadius: 8,
    fontSize: 16, fontWeight: 600, textAlign: "center", outline: "none",
    letterSpacing: "0.1em", marginBottom: 16,
  },
  modalActions: { display: "flex", gap: 8, justifyContent: "flex-end" },
  modalCancel: {
    padding: "8px 20px", background: "#fff", color: "#666", border: "1px solid #e5e5e5",
    borderRadius: 6, fontSize: 14, fontWeight: 500,
  },
  modalConfirm: {
    padding: "8px 20px", background: "#dc2626", color: "#fff", border: "none",
    borderRadius: 6, fontSize: 14, fontWeight: 600,
  },
  modalConfirmDisabled: {
    padding: "8px 20px", background: "#fca5a5", color: "#fff", border: "none",
    borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: "not-allowed",
  },
};
